import { Errors } from '@/enums/errors.enum';
import { Setting } from '@/interfaces/setting';
import { SettingResource, settingResource } from '@/resources/setting.resource';
import { settings } from 'cluster';
import { EntityModule } from './entity.module';
import attachment from './setting/attachment.module';

/**
 * Moduł Ustawień
 *
 *
 * @export
 * @class SettingModule
 * @extends {EntityModule<Setting, SettingResource>}
 */
export class SettingModule extends EntityModule<Setting, SettingResource> {
  public modules = { attachment };

  constructor() {
    super(settingResource);
    this.state = {
      ...this.state,
      settings: {}
    } as any;

    this.mutations = {
      ...this.mutations,

      /**
       * Zapisuje wartość ustawienia o danym kodzie
       */
      saveByCode: (localState: any, setting: Setting): void => {
        if (setting.code) {
          localState.settings = { ...localState.settings, [setting.code]: setting };
        }
      },

      /**
       * Usuwa ustawienie o danym kodzie
       *
       * @param {*} localState
       * @param {string} code
       */
      deleteByCode: (localState: any, code: string): void => {
        if (code) {
          localState.settings = { ...localState.settings, [code]: undefined };
        }
      }
    };

    this.actions = {
      ...this.actions,
      /**
       * Szukanie ustawienia po kodzie
       */
      findPublic: (context: any, data: any): Promise<Setting | void> =>
        this.resource.findPublic(data.code, data.entityType, data.entityId).then(setting => {
          if (setting) {
            context.commit('saveByCode', setting);
            return setting;
          }
          context.commit('deleteByCode', data.code);
        }),

      /**
       * Pobiera ustawienie po kodzie i łączy je z domyślnym ustawieniem
       *
       * @param {*} context
       * @param {*} data
       * @returns {Promise<Setting>}
       */
      findPublicWithDefault: (context: any, data: any): Promise<Setting> =>
        this.resource
          .findPublic(data.code, data.entityType, data.entityId)
          .then(setting => {
            if (!setting?.value && data.throwOnEmpty) {
              throw new Error(Errors.SETTING_NOT_FOUND);
            }
            return setting || ({} as Setting);
          })
          .then(entitySetting =>
            this.resource.findPublic(data.code).then(defaultSetting => {
              const defaultSettingValue = defaultSetting?.value && JSON.parse(defaultSetting.value);
              const entitySettingValue = entitySetting.value && JSON.parse(entitySetting.value);

              if (Array.isArray(defaultSettingValue) || Array.isArray(entitySettingValue)) {
                defaultSetting.value = [...(defaultSettingValue || []), ...(entitySettingValue || [])] as any;
              } else {
                defaultSetting.value = { ...defaultSettingValue, ...entitySettingValue } as any;
              }

              return defaultSetting;
            })
          )
          .then(setting => {
            context.commit('saveByCode', setting);
            return setting;
          })
          .catch(err => {
            if (!data.noDelete) {
              context.commit('deleteByCode', data.code);
            }
            throw err;
          }),

      /**
       * Pobiera listę publicznych ustawień
       *
       * @param context context
       * @param filters filtry listy
       */
      publicList: (context: any, filters: any): Promise<Setting[]> => {
        return this.resource.publicList({ count: -1, ...filters }).then(({ list, count }) => {
          list.forEach(setting => context.commit('saveByCode', setting));

          if (filters.codes?.length) {
            (filters.codes as string[])
              .filter(code => !list.some(setting => setting.code === code))
              .forEach(code => context.commit('deleteByCode', code));
          }

          return list;
        });
      }
    };
  }
}

export default new SettingModule();
